<template>
  <div class="generative-ai-usage-prompt-logs-table">
    <div class="generative-ai-usage-prompt-logs-table__header">
      <p class="header-title">Prompt Logs</p>
      <div class="dropdown-sorting">
        <button class="dropdown-sorting__trigger" @click="toggleDropdownSortingMenu">
          <p>{{ selectedSortOption.label }}</p>
          <i class="bx bxs-down-arrow" :class="{ 'upside-arrow': showDropdownSortingMenu }"></i>
        </button>
        <div v-if="showDropdownSortingMenu && Array.isArray(sortOptions)" class="dropdown-sorting__menu">
          <div
            v-for="(sortOption, index) of sortOptions"
            :key="`sort-by-${index}`"
            :class="`menu-item${ selectedSortOption.value === sortOption.value ? '--selected' : ''}`"
            @click="handleSelectSorting(sortOption.value)"
          >
            {{ sortOption.label }}
          </div>
        </div>
      </div>
    </div>
    <div ref="tableWrapperRef" class="table-wrapper">
      <table class="table-content">
        <thead>
          <tr>
            <th style="width: 16.7%">Date/Time</th>
            <th style="width: 16.7%">Status</th>
            <th style="width: 16.7%">Response Time</th>
            <th style="width: 16.7%">#Input Tokens</th>
            <th style="width: 16.7%">#Output Tokens</th>
            <th style="width: 16.7%" />
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr>
            <td colspan="5" class="table-loading-content">
              <div class="loading-section">
                <div class="spinner-border" role="status"></div>
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="Array.isArray(promptLogList) && promptLogList.length > 0">
          <tr v-for="promptLog of promptLogList" :key="`prompt-log-${promptLog.id}`">
            <td>{{ formatDate(promptLog.created_at) }}</td>
            <td>
              <div class="badge badge-pill badge-success" v-if="formatStatus(promptLog.http_response_code) === 'success'">
                <p>Success</p>
              </div>
              <div class="badge badge-pill badge-failed" v-else-if="formatStatus(promptLog.http_response_code) === 'failed'">
                <p>Failed</p>
              </div>
            </td>
            <td>{{ formatNumber(promptLog.http_response_time/1000) }} s
            </td>
            <td>
              {{ formatNumber(promptLog.input_token) }}
            </td>
            <td>
              {{ formatNumber(promptLog.output_token) }}
            </td>
            <td>
              <div @click="openPromptLogModal(promptLog)" class="show-detail-icon p-0 ml-2">
                <i class="bx bx-show"></i>
              </div>
            </td>
          </tr>
          <tr>
            <td colspan="6" class="infinite-loading-section">
              <infinite-loading @infinite="infiniteHandler" force-use-infinite-wrapper=".table-wrapper" :distance=10>
                <span slot="spinner">
                  <i class='bx bx-loader-alt bx-spin'></i>
                </span>
                <span slot="no-more"></span>
                <span slot="no-results"></span>
              </infinite-loading>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="6">
              <NoResultsFound/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!--Input-Output Message Modal -->
    <div v-if="showPromptLogModal" class="input-output-message-modal">
      <div class="modal-card">
        <div class="modal-card-header">
          <div class="message-header-wrapper">
            <i class="bx bx-log-in-circle"></i>
            <p>Prompt</p>
          </div>
          <div class="message-header-wrapper">
            <i class="bx bx-log-in-circle"></i>
            <p>Response</p>
          </div>
          <i @click="closePromptLogModal" class="bx bx-x close-icon-btn"> </i>
        </div>
        <div class="modal-card-content">
          <div class="message-content">
            {{ selectedPromptLog.input }}
          </div>
          <div class="message-content">
            {{ selectedPromptLog.output }}
          </div>
        </div>
        <div class="modal-card-footer">
          <button @click="closePromptLogModal" class="modal-close-btn">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { DateTime } from 'luxon';
import numeral from 'numeral';
import InfiniteLoading from 'vue-infinite-loading';
import NoResultsFound from '../NoResultsFound.vue';

export default {
  name: 'GenAIUsagePromptLogsTable',
  components: {
    InfiniteLoading,
    NoResultsFound,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    selectedSortOption: {
      type: Object,
      default: () => {},
    },
    promptLogList: {
      type: Array,
      default: () => [],
    },
    promptLogScrollRef: {
      type: Element,
    }
  },
  data() {
    return {
      showDropdownSortingMenu: false,
      selectedPromptLog: null,
      showPromptLogModal: false,
    };
  },
  methods: {
    toggleDropdownSortingMenu() {
      this.showDropdownSortingMenu = !this.showDropdownSortingMenu;
    },
    resetPromptLogTableScrollPosition() {
      const scrollContainer = this.$refs.tableWrapperRef;
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
    },
    handleSelectSorting(value) {
      if (value === this.selectedSortOption.value) return;
      const selectedSorting = this.sortOptions.find((sortItem) => sortItem.value === value);
      if (!selectedSorting) return;
      this.$emit('on-select-sort-option', selectedSorting);
      this.showDropdownSortingMenu = false;
    },
    infiniteHandler($state) {
      this.$emit('on-scroll-to-load', $state);
    },
    openPromptLogModal(promptLog) {
      let isSelectNewPromptLog = true;
      if (this.selectedPromptLog && promptLog.id === this.selectedPromptLog.id) {
        isSelectNewPromptLog = false;
      }
      if (isSelectNewPromptLog) {
        this.selectedPromptLog = promptLog;
      }
      this.showPromptLogModal = true;
    },
    closePromptLogModal() {
      this.showPromptLogModal = false;
    },
    formatDate(unixTimeMilliseconds) {
      return DateTime.fromMillis(unixTimeMilliseconds).setZone('Asia/Bangkok').toFormat('dd/MM/yyyy HH:mm:ss');
    },
    formatStatus(httpRespCode) {
      const httpRespCodeNum = Number(httpRespCode);

      if (!httpRespCodeNum) return 'failed';
      return httpRespCodeNum >= 200 && httpRespCodeNum < 300 ? 'success' : 'failed';
    },
    formatNumber(value) {
      return numeral(value).format('0.[0]a');
    },
  },
  watch: {
    promptLogList: {
      immediate: true,
      handler(newValue) {
        this.promptLogList = newValue;
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.generative-ai-usage-prompt-logs-table {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
  border-radius: 16px;
  padding: 12px 20px;
  p {
    margin-bottom: 0px;
  }
  i {
    font-size: 16px;
  }
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .header-title {
      color: #111b49;
      font-size: 16px;
      font-weight: 600;
      line-height: 19.2px;
    }
    .dropdown-sorting {
      position: relative;
      &__trigger {
        background-color: #fff;
        color: #111b49;
        border: 1px solid #dadce4;
        border-radius: 8px;
        padding: 8px 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        line-height: 24px;
        outline: none;
        width: 180px;
        .bxs-down-arrow {
          color: #868ba6;
          font-size: 12px;
          margin-left: 8px;
        }
        .upside-arrow {
          rotate: 180deg;
        }
      }
      &__menu {
        border: 1px solid #dadce4;
        border-radius: 8px;
        background-color: #fff;
        padding-block: 12px;
        font-size: 14px;
        font-weight: 400;
        line-height: 21px;
        position: absolute;
        z-index: 2;
        display: flex;
        flex-direction: column;
        width: 180px;
        margin-top: 4px;
        right: 0;
        > div {
          padding: 8px 12px;
          width: 100%;
          cursor: pointer;
        }
        > .menu-item {
          color: #2962df;
          &--selected {
            background-color: #f5f6fa;
            color: #4b5178;
          }
        }
      }
    }
  }
  .table-wrapper {
    margin-top: 12px;
    height: 747px;
    overflow-y: auto;
    .table-content {
      width: 100%;
      color: #3d4b65;
      letter-spacing: 0%;
      table-layout: fixed;
      height: 100%;
      thead {
        position: sticky;
        top: 0;
        z-index: 1;
        box-shadow: 0px 1px 0px 0px #9ea5b2;
        tr {
          th {
            background-color: #fafafc;
            padding: 12px 16px;
            font-size: 12px;
            font-weight: 400;
            text-align: left;
            margin-top: auto;
            margin-bottom: auto;
            line-height: 18px;
          }
        }
      }
      .table-loading-content{
        height: 100%;
        width: 100%;
        .loading-section{
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
          width: 100%;
        }
      }
      tbody {
        tr {
          td {
            padding: 16px 16px 12px 16px;
            font-weight: 400;
            font-size: 14px;
            line-height: 20px;
          }
        }
        tr:not(:last-child) {
          td {
            border-bottom: 1px solid #e7e8ee;
          }
        }
        .badge-success {
          background-color: #50cd89;
          color: #fff;
        }
        .badge-failed {
          background-color: #ef6767;
          color: #fff;
        }
        .show-detail-icon {
          cursor: pointer;
        }
      }
      .no-prompt-log-data{
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        color: #666D92;
        height: 100%;
        img{
          width: 80px;
          height: 91px;
          margin-bottom: 18px;
        }
        .text-title{
          font-weight: 500;
          font-size: 16px;
          line-height: 19.2px;
          letter-spacing: 0px;
        }
        .text-subtitle{
          font-weight: 400;
          font-size: 14px;
          line-height: 21px;
          letter-spacing: 0px;
        }
      }
    }
  }
  .input-output-message-modal {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 1040;
    padding: 4% 10% 8% 20%;
    background-color: rgba(0, 0, 0, 0.5);
    .modal-card {
      background-color: #fff;
      width: 100%;
      height: 100%;
      border-radius: 16px;
      padding: 20px;
      overflow: hidden;
      .modal-card-header {
        display: flex;
        align-items: center;
        .message-header-wrapper {
          display: flex;
          align-items: center;
          flex: 1;
          .bx-log-in-circle {
            margin-right: 8px;
            color: #3371ff;
            font-size: 24px;
          }
          p {
            color: #0d173c;
            font-weight: 600;
            font-size: 20px;
            line-height: 120%;
          }
        }
        .close-icon-btn {
          cursor: pointer;
          font-size: 24px;
        }
      }
      .modal-card-content {
        display: flex;
        flex-grow: 1;
        height: 100%;
        gap: 16px;
        padding-top: 20px;
        padding-right: 24px;
        overflow: hidden;
        .message-content {
          background-color: #fafafc;
          color: #646a8c;
          border-radius: 8px;
          width: 100%;
          height: 100%;
          flex: 1;
          overflow-y: scroll;
          font-weight: 400;
          font-size: 13px;
          line-height: 150%;
          padding: 8px;
        }
      }
      .modal-card-footer {
        display: flex;
        justify-content: end;
        padding-top: 20px;
        .modal-close-btn {
          border-radius: 24px;
          border: none;
          padding: 8px 16px;
          background-color: #3371ff;
          color: #ffffff;
          font-weight: 500;
          font-size: 14px;
          line-height: 24px;
          outline: none;
          &:hover {
            cursor: pointer;
          }
        }
      }
    }
  }
  .infinite-loading-section{
    height: 40px;
  }
}
</style>
